import { computed } from 'vue';
import i18n from '@/setup/i18n.js';
import useNotificationStore from '@/stores/notification';
import useSessionStore from '@/stores/session';

export const isLoggedIn = computed(() => useSessionStore().getIsLoggedIn);

/**
 * Checks if CDC and especially the accounts on cdc is available or not.
 *
 * @returns {Boolean}
 */
export function isCdcAvailable() {
  if (window?.gigya?.accounts === undefined) {
    // eslint-disable-next-line no-console
    console.error(`CDC Error: no cdc exists ${!!window.gigya}`);
  }

  return !!window.gigya?.accounts;
}

/**
 * Opens the CDC screen set with the given options.
 * For all available options see: https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/413a5b7170b21014bbc5a10ce4041860.html#description
 *
 * The following are the required options:
 *
 * @param {Object} options - The options to be used.
 * @param {String} options.screenSet - The screen set to be shown.
 * @param {String} options.startScreen - The start screen to be shown.
 * @param {String} options.lang - The language to be used.
 * @param {String} options.deviceType - The device type to be used.
 */
export function showCDCScreenSet(options) {
  const defaultOptions = {
    lang: i18n.global.locale,
    deviceType: 'auto',
  };

  window.gigya?.accounts?.showScreenSet({ ...defaultOptions, ...options });
}

/**
 * Checks if the user has a valid session.
 *
 * @returns {Promise}
 */
export function verifySession() {
  return new Promise((resolve, reject) => {
    if (!isCdcAvailable()) {
      reject(new Error('CDC not loaded'));
    }

    window.gigya?.accounts?.session.verify({
      callback: (response) => {
        if (response.status === 'OK') {
          resolve(response);
        } else {
          reject(new Error('CDC session verify: invalid session'));
        }
      },
    });
  });
}

/**
 * Fetches the Account info.
 *
 * @param {Object} options - The options to be used.
 * @param {String} options.include - Define which infos should be returned.
 *
 * @returns {Promise}
 */
export function getAccountInfo(options) {
  return new Promise((resolve, reject) => {
    if (!isCdcAvailable()) {
      reject(new Error('CDC not loaded'));
    }

    if (window?.gigya?.accounts?.getAccountInfo === undefined) {
      reject(new Error('CDC Error getAccountInfo: function not callable'));
    }

    window.gigya?.accounts?.getAccountInfo({
      ...options,
      callback: (response) => {
        if (response.UID) {
          resolve(response);
        } else {
          // eslint-disable-next-line no-console
          console.info('CDC getAccountInfo: we did not receive an UID.');

          reject(response);
        }
      },
    });
  });
}

/**
 * Saves the Account info.
 *
 * @param {Object} options - The options to be used.
 *
 * @returns {Promise}
 */
export function setAccountInfo(options) {
  const debugLogPrefix = 'CDC Error setAccountInfo:';

  return new Promise((resolve, reject) => {
    if (window?.gigya?.accounts?.setAccountInfo === undefined) {
      // eslint-disable-next-line no-console
      console.error(`${debugLogPrefix} function not callable. gigya is loaded: ${!!window.gigya}`);
    }

    window.gigya?.accounts?.setAccountInfo({
      ...options,
      callback: (response) => {
        if (response) {
          resolve(response);
        } else {
          // eslint-disable-next-line no-console
          console.error(`${debugLogPrefix} we did not get a response.`);

          reject(response);
        }
      },
    });
  });
}

/**
 * Returns a user object by a CDC payload.
 *
 * @param {Object} user - some given userdata.
 *
 * @returns {Object}
 */
export function getUserByPayload(user) {
  if (!user || !user.UID || !user?.profile?.email) {
    // eslint-disable-next-line no-console
    console.error(`CDC Data-Check, did not receive required data: UID: ${user?.UID}, email: ${user?.profile?.email}`);
  }

  const payload = {
    accountType: user?.data?.accountType,
    firstName: user?.profile?.firstName,
    lastName: user?.profile?.lastName,
    function: user?.data?.fromUser?.function,
    industry: user?.data?.fromUser?.companyIndustry,
    UID: user?.UID,
    email: user?.profile?.email,
    UIDSignature: user?.UIDSignature,
    signatureTimestamp: user?.signatureTimestamp,
    companyCountry: user?.data?.fromUser?.companyCountry,
  };

  if (Object.values(payload).includes(null) || Object.values(payload).includes(undefined)) {
    const missingProperties = Object.keys(payload)
      .map((key) => (payload[key] === null ? key : null))
      .filter(Boolean);

    // eslint-disable-next-line no-console
    console.error(`CDC Data-Check, missing properties: ${missingProperties.join(',')}`);
  }

  return payload;
}

/**
 * Shows a notification to the user, that something is wrong.
 *
 * @param {String} message - The error message.
 */
export function showErrorNotification(message) {
  useNotificationStore().showNotification({
    expire: false,
    message: {
      type: 'error',
      message,
    },
  });
}
